// src/pages/SystemsPage.jsx
import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// лёгкие эффекты
const float = keyframes`
  0% { transform: translateY(0) }
  50% { transform: translateY(-6px) }
  100% { transform: translateY(0) }
`;
const shimmer = keyframes`
  0% { background-position: 0% 50% }
  100% { background-position: 200% 50% }
`;

const Page = styled.div`
  min-height: 100dvh;
`;

const Hero = styled.header`
  position: relative;
  overflow: hidden;
  padding: clamp(40px, 6vw, 80px) 0;
  isolation: isolate;
  .bg {
    position: absolute; inset: -20%;
    background: radial-gradient(40% 40% at 10% 0%, #ff660022, transparent 60%),
                radial-gradient(35% 35% at 90% 10%, #00c2ff22, transparent 60%),
                radial-gradient(55% 55% at 50% 120%, #a8ff001f, transparent 60%);
    filter: blur(42px);
    transform: translateZ(0);
    z-index: -1;
  }
  .wrap {
    width: ${({theme})=>theme.container}; margin: 0 auto;
    display: grid; grid-template-columns: 1fr;
    gap: 16px;
  }
  h1{ margin: 0; font-size: ${({theme})=>theme.font.sizes.h1}; }
  p.lead{ margin: 0; color: ${({theme})=>theme.textMuted}; font-size: clamp(16px, 1.8vw, 20px); }
`;

const Layout = styled.div`
  width: ${({theme})=>theme.container};
  margin: 0 auto;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 18px;
  @media (max-width: 1100px){ grid-template-columns: 1fr; }
`;

const Toc = styled.nav`
  position: sticky; top: 80px;
  align-self: start;
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  padding:12px;
  box-shadow:${({theme})=>theme.shadow.sm};
  .kicker{ font-size:13px; opacity:.7; margin-bottom:6px; text-transform:uppercase; letter-spacing:.12em; }
  a{
    display:flex; align-items:center; gap:8px;
    padding:8px 10px; margin:2px 0; border-radius:${({theme})=>theme.radius.md}px;
    color:inherit; text-decoration:none; border:1px solid transparent;
  }
  a:hover{ background:${({theme})=>theme.body}; border-color:${({theme})=>theme.border}; }
  a.active{ outline:2px solid ${({theme})=>theme.accent}; }
`;

const Section = styled.section`
  scroll-margin-top: 90px; /* чтобы при якоре не прятался заголовок под шапкой */
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px;
  box-shadow:${({theme})=>theme.shadow.sm};
  padding:18px;
  margin-bottom: 14px;

  .head{
    display:flex; align-items:center; justify-content:space-between; gap: 12px; flex-wrap:wrap;
    margin: 2px 0 8px;
  }
  .title{
    display:flex; align-items:center; gap:10px;
  }
  .title .icon{
    width:42px; height:42px; border-radius:12px; display:grid; place-items:center;
    background:${({theme})=>theme.body}; border:1px solid ${({theme})=>theme.border};
    animation:${float} 6s ease-in-out infinite;
  }
  .subtitle{ color:${({theme})=>theme.textMuted}; margin: 0; }
  .grid{
    display:grid; grid-template-columns: 1.2fr .8fr; gap: 14px;
    @media (max-width: 980px){ grid-template-columns: 1fr; }
  }
  .text{
    display:grid; gap:10px; align-content:start;
  }
  .callout{
    border:1px dashed ${({theme})=>theme.border}; background:${({theme})=>theme.body};
    border-radius:${({theme})=>theme.radius.md}px; padding:12px;
  }
  .gallery{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
    border-radius:${({theme})=>theme.radius.lg}px; padding:10px;
    display:grid; gap:10px;
    grid-template-columns: 1fr 1fr;
  }
  .gallery img{
    width:100%; height:180px; object-fit:cover; border-radius:${({theme})=>theme.radius.md}px;
  }
  ul{ margin:0; padding-left:18px; }
`;

const Articles = styled.section`
  margin: 24px 0 12px;
  .wrap{ border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:16px; box-shadow:${({theme})=>theme.shadow.sm}; }
  .list{
    display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 12px;
    @media (max-width: 980px){ grid-template-columns: 1fr; }
  }
  .a{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
    border-radius:${({theme})=>theme.radius.lg}px; padding:12px; display:flex; gap:10px;
  }
  .thumb{ width:120px; height:80px; border-radius:${({theme})=>theme.radius.md}px; object-fit:cover;
    background:#eee; animation:${shimmer} 5s linear infinite; background-image:linear-gradient(90deg,#0000,#0001,#0000); background-size:200% 100%; }
  .meta b{ display:block; }
  .meta span{ font-size:13px; opacity:.7; }
`;

const Extra = styled.section`
  display:grid; gap:14px; margin: 18px 0 24px;
  .block{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:16px; box-shadow:${({theme})=>theme.shadow.sm};
  }
  h3{ margin:0 0 6px; }
  .two{
    display:grid; grid-template-columns: 1fr 1fr; gap: 12px;
    @media (max-width: 980px){ grid-template-columns: 1fr; }
  }
  .steps{ counter-reset:s; display:grid; gap:8px; }
  .steps li{ list-style:none; counter-increment:s;
    border:1px dashed ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.md}px;
    padding:10px 12px; }
  .steps li b::before{ content: counter(s) '. '; color:${({theme})=>theme.accent}; }
  .faq .q{
    border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden;
  }
  .faq button{
    width:100%; text-align:left; padding:12px; background:none; border:none; cursor:pointer;
    display:flex; align-items:center; justify-content:space-between; gap:8px; font-weight:700;
  }
  .faq .a{ padding:0 12px 12px; color:${({theme})=>theme.textMuted}; }
`;

const Icon = ({name, size=22}) => {
  const map = {
    cctv:     <path d="M3 10h10l2 3H3v-3zm0-4h14v3H3V6zm16 6l2 2-4 4-2-2 4-4z" />,
    acs:      <path d="M12 2a5 5 0 00-5 5v3H5v8h14v-8h-2V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z" />,
    gates:    <path d="M3 7h4v10H3V7zm14 0h4v10h-4V7zM9 7h6v10H9V7z" />,
    electro:  <path d="M13 2L4 14h6v8l9-12h-6V2z" />,
    water:    <path d="M12 3l5 8a5 5 0 11-10 0l5-8z" />,
    k1k2:     <path d="M4 4h16v4H4V4zm0 6h12v4H4v-4zm14 0h2v10h-2V10z" />,
    vent:     <path d="M12 3a9 9 0 100 18 9 9 0 000-18zm1 8h6a7 7 0 01-6 6v-6z" />,
    core:     <path d="M4 12l8-8 8 8-8 8-8-8zm5-1l3 3 5-5" />,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">{map[name]||map.cctv}</svg>;
};

// контент по разделам (изображения — плейсхолдеры/замени на свои)
const BLOCKS = [
  {
    id: 'cctv',
    title: 'Відеоспостереження',
    icon: 'cctv',
    lead: 'Проєктування і монтаж CCTV систем: від 4 до 256+ камер, запис, аналітика, інтеграції.',
    bullets: [
      'IP/TVI/AHD — підбираємо оптимум по бюджету/якісті',
      'NVR з дисковими масивами, резервування живлення',
      'Відеоаналітика: розпізнавання облич, номерів, периметр',
      'Інтеграція з СКД та охоронною сигналізацією',
    ],
    gallery: ['/img/systems/cctv1.jpg','/img/systems/cctv2.jpg'],
    more: 'Виконуємо мережеву розводку, PoE, налаштовуємо доступ з мобільних та хмарне резервування.',
  },
  {
    id: 'acs',
    title: 'Контроль доступу',
    icon: 'acs',
    lead: 'СКД для офісів, складів і виробництв: турнікети, шлагбауми, електрозамки, облік робочого часу.',
    bullets: [
      'RFID/Mifare, біометрія, BLE/NFC',
      'Антипасбек, зони доступу, віддалене адміністрування',
      'Інтеграція з відео та пожежною системою',
    ],
    gallery: ['/img/systems/acs1.jpg','/img/systems/acs2.jpg'],
    more: 'Працюємо з 1–1000+ точок доступу, з окремими контролерами чи центральним сервером.',
  },
  {
    id: 'gates',
    title: 'Ворота та автоматика',
    icon: 'gates',
    lead: 'Розсувні, секційні, відкатні ворота, рольставні, шлагбауми. Повний комплекс під ключ.',
    bullets: [
      'Автоматика з дистанційним керуванням і GSM',
      'Фотоелементи, сигнальні лампи, захист від затиску',
      'Сервіс і регламентне обслуговування',
    ],
    gallery: ['/img/systems/gates1.jpg','/img/systems/gates2.jpg'],
    more: 'Підбираємо профілі, фурнітуру та приводи під вагу/вітрове навантаження.',
  },
  {
    id: 'electro',
    title: 'Електромонтаж',
    icon: 'electro',
    lead: 'Внутрішні/зовнішні електромонтажні роботи до 1000 В: щити, кабельні траси, освітлення, заземлення.',
    bullets: [
      'Щитові рішення, АВР, релейний/PLC контроль',
      'Розрахунок навантажень, PUE/ДБН відповідність',
      'Паспортизація, протоколи вимірювань',
    ],
    gallery: ['/img/systems/electro1.jpg','/img/systems/electro2.jpg'],
    more: 'Виконуємо аудит, пусконалагодження та оформлюємо виконавчу документацію.',
  },
  {
    id: 'water',
    title: 'Водопостачання та опалення',
    icon: 'water',
    lead: 'Інженерні мережі: вода, опалення, ІТП, котельні, циркуляція, колектори.',
    bullets: [
      'PPR, мідь, нержавіюча сталь, прес-фітинги',
      'Насосні групи, балансування, автоматика',
      'Теплові пункти і погодне регулювання',
    ],
    gallery: ['/img/systems/water1.jpg','/img/systems/water2.jpg'],
    more: 'Розрахунок тепловтрат, підбір обладнання, сервіс.',
  },
  {
    id: 'k1k2',
    title: 'К1/К2 димовидалення',
    icon: 'k1k2',
    lead: 'Системи димовидалення та підпору повітря відповідно до норм ДБН та ДСТУ.',
    bullets: [
      'Проєктування, аеродинамічні розрахунки',
      'Димові клапани, вентилятори, автоматика',
      'Інтеграція з системами пожежної сигналізації',
    ],
    gallery: ['/img/systems/k1k2_1.jpg','/img/systems/k1k2_2.jpg'],
    more: 'Постанова на обслуговування та періодичні перевірки.',
  },
  {
    id: 'vent',
    title: 'Вентиляція',
    icon: 'vent',
    lead: 'Припливно-витяжні системи, фільтрація, рекуперація, повітроводи та ШВЛ при необхідності.',
    bullets: [
      'Розрахунок кратності та перепадів тиску',
      'Шумоглушення, віброізоляція',
      'Системи з рекуперацією тепла',
    ],
    gallery: ['/img/systems/vent1.jpg','/img/systems/vent2.jpg'],
    more: 'Під ключ: від проєкту до пусконалагодження та сервісу.',
  },
  {
    id: 'core',
    title: 'Алмазне свердління',
    icon: 'core',
    lead: 'Алмазне свердління/різання бетону, залізобетону, цегли. Чисто і точно.',
    bullets: [
      'Отвори Ø20–500+ мм, глибина за узгодженням',
      'Свердління під інженерні мережі, вентиляцію, анкера',
      'Мінімум пилу і вібрацій, охолодження',
    ],
    gallery: ['/img/systems/core1.jpg','/img/systems/core2.jpg'],
    more: 'Працюємо в діючих приміщеннях з локальною ізоляцією зони робіт.',
  },
];

const FAQ_EXTRA = [
  { q: 'Скільки в середньому триває проєкт “під ключ”?',
    a: 'Типово 2–6 тижнів: 3–7 днів — обстеження й проєкт; 1–2 тижні — поставка; 2–10 днів — монтаж і ПНР. Великі об’єкти узгоджуємо окремо.' },
  { q: 'Які умови оплати?',
    a: 'Передплата 50–70% на закупівлю матеріалів/обладнання, решта — по етапах або після ПНР. Можливі фіксовані графіки платежів у договорі.' },
  { q: 'Чи надаєте документацію та гарантію?',
    a: 'Так. Виконавча документація (схеми, паспорти, протоколи), інструктаж. Гарантія на роботи й обладнання відповідно до договору (12+ міс.).' },
  { q: 'Чи працюєте по вихідних/уночі?',
    a: 'Так, за домовленістю. Маємо досвід робіт без зупинки виробництва.' },
  { q: 'Чи допоможете з авторським/технаглядом?',
    a: 'Так, беремо на себе координацію суміжних підрядників, технічний нагляд, шефмонтаж.' },
];

export default function SystemsPage(){
  const meta = usePageMeta('systems');
  const location = useLocation();
  const [active, setActive] = useState(location.hash?.slice(1) || 'cctv');
  const [openFaq, setOpenFaq] = useState(-1);

  // AOS и плавный скролл
  useEffect(()=>{
    AOS.init({ once: true, duration: 700, offset: 80, easing: 'ease-out-cubic' });
  },[]);
  useEffect(()=>{
    // переход по якорю при загрузке/смене
    const jump = (hash)=>{
      const id = (hash||'').replace('#','');
      if(!id) return;
      const el = document.getElementById(id);
      if(el){ el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActive(id); }
    };
    jump(location.hash);
    const onHash = ()=> jump(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return ()=> window.removeEventListener('hashchange', onHash);
  },[location.hash]);

  // наблюдаем за заголовками, чтобы подсвечивать пункт в ToC
  useEffect(()=>{
    const obs = new IntersectionObserver((entries)=>{
      entries.forEach(e=>{
        if(e.isIntersecting){ setActive(e.target.id); }
      });
    }, { rootMargin: '-50% 0px -40% 0px', threshold: 0.01 });

    BLOCKS.forEach(b=>{
      const el = document.getElementById(b.id);
      if(el) obs.observe(el);
    });
    return ()=> obs.disconnect();
  },[]);

  const articles = [
    { t:'Чому IP-камери краще для складу', img:'/img/articles/a1.jpg', to:'/articles' },
    { t:'Як обрати автоматику для воріт', img:'/img/articles/a2.jpg', to:'/articles' },
    { t:'Електрощит: 5 помилок у підрядників', img:'/img/articles/a3.jpg', to:'/articles' },
  ];

  return (
    <Page>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/systems'}
      />
      <Hero>
        <div className="bg" aria-hidden />
        <div className="wrap" data-aos="fade-up">
          <h1>Інженерні системи</h1>
          <p className="lead">Проєктування, монтаж і сервіс: відеоспостереження, СКД, ворота, електрика, вода/тепло, димовидалення, вентиляція, алмазне свердління.</p>
        </div>
      </Hero>

      <Layout>
        {/* TOC */}
        <Toc aria-label="Навігація по розділах">
          <div className="kicker">Розділи</div>
          {BLOCKS.map(b=>(
            <a key={b.id} href={`#${b.id}`} className={active===b.id?'active':''}>
              <Icon name={b.icon} />
              <span>{b.title}</span>
            </a>
          ))}
        </Toc>

        {/* CONTENT */}
        <div>
          {BLOCKS.map((b, idx)=>(
            <Section id={b.id} key={b.id} data-aos="fade-up" data-aos-delay={idx*40}>
              <div className="head">
                <div className="title">
                  <div className="icon"><Icon name={b.icon} /></div>
                  <div>
                    <h2 style={{margin:'0'}}>{b.title}</h2>
                    <p className="subtitle">{b.lead}</p>
                  </div>
                </div>
                <Link className="btn btn--ghost" to={`/contacts?topic=${b.id}`}>Замовити консультацію</Link>
              </div>

              <div className="grid">
                <div className="text">
                  <div className="callout">
                    <b>Що входить:</b>
                    <ul>
                      {b.bullets.map((x,i)=><li key={i}>{x}</li>)}
                    </ul>
                  </div>
                  <p>{b.more}</p>
                  <p style={{color:'var(--textMuted)'}}>Працюємо по Києву та Україні. Можлива поетапна реалізація: від експрес-проєкту до повного комплексу з авторським наглядом.</p>
                </div>
                <div className="gallery" aria-label="Галерея">
                  <img src={b.gallery[0]} alt="" loading="lazy" />
                  <img src={b.gallery[1]} alt="" loading="lazy" />
                </div>
              </div>
            </Section>
          ))}

          {/* Статті після блоків */}
          <Articles>
            <div className="wrap" data-aos="fade-up">
              <div className="head" style={{display:'flex', justifyContent:'space-between', alignItems:'end', marginBottom:12}}>
                <div>
                  <div className="kicker" style={{opacity:.7, textTransform:'uppercase', letterSpacing:'.12em', fontSize:13}}>Матеріали</div>
                  <h2 style={{margin:'0'}}>Статті та кейси</h2>
                </div>
                <Link className="btn btn--ghost" to="/articles">Усі статті</Link>
              </div>
              <div className="list">
                {articles.map((a,i)=>(
                  <Link to={a.to} className="a" key={i}>
                    <img className="thumb" src={a.img} alt="" loading="lazy" />
                    <div className="meta">
                      <b>{a.t}</b>
                      <span>Читати →</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Articles>

          {/* Додаткові блоки: як працюємо, оплата, терміни, FAQ, гарантія */}
          <Extra>
            <div className="block" data-aos="fade-up">
              <h3>Як ми працюємо</h3>
              <ul className="steps">
                <li><b>Заявка та бриф</b> — коротко описуєте задачу, прикріплюєте план/ТЗ/фото.</li>
                <li><b>Обстеження</b> — виїзд або дистанційний аудит, фіксуємо обмеження.</li>
                <li><b>Рішення та кошторис</b> — 1–2 варіанти з порівнянням за бюджетом/надійністю.</li>
                <li><b>Договір</b> — узгоджуємо графік та умови — стартуємо.</li>
                <li><b>Поставка</b> — замовляємо обладнання/матеріал, готуємо виробництво.</li>
                <li><b>Монтаж і ПНР</b> — чистий монтаж, маркування, налаштування, інструктаж.</li>
                <li><b>Здача та сервіс</b> — акти, виконавча документація, гарантія, регламенти ТО.</li>
              </ul>
            </div>

            <div className="two">
              <div className="block" data-aos="fade-up">
                <h3>Монтаж і оплата</h3>
                <p><b>Оплата:</b> передплата 50–70% (закупівля), далі — по етапах або після ПНР. Безготівка з ПДВ/без.</p>
                <p><b>Графік робіт:</b> будні 9:00–18:00, можливий нічний/вихідний режим за погодженням.</p>
                <p><b>Доступ/допуски:</b> проходимо інструктаж, працюємо за правилами об’єкта (ПБ, ОП, ТБ).</p>
              </div>
              <div className="block" data-aos="fade-up" data-aos-delay="60">
                <h3>Скільки триває проєкт “під ключ”</h3>
                <p>Середній горизонт — <b>2–6 тижнів</b>:</p>
                <ul>
                  <li>Обстеження та рішення — 3–7 днів</li>
                  <li>Поставка обладнання — 1–2 тижні</li>
                  <li>Монтаж і ПНР — 2–10 днів</li>
                </ul>
                <p style={{color:'var(--textMuted)'}}>Великі/багатосекційні об’єкти — по окремому графіку з поетапним введенням.</p>
              </div>
            </div>

            <div className="two">
              <div className="block" data-aos="fade-up">
                <h3>Гарантія та сервіс</h3>
                <p>Гарантія на роботи — <b>12 міс.</b> (або за договором), на обладнання — згідно виробника. Пропонуємо сервісні контракти: щоквартальне ТО, пріоритетний виїзд, склад ЗІП.</p>
              </div>
              <div className="block" data-aos="fade-up" data-aos-delay="60">
                <h3>Документація та відповідність</h3>
                <p>Виконавча документація: схеми, специфікації, сертифікати, протоколи вимірювань. Відповідність ДБН/ДСТУ/ПУЕ, інтеграція з існуючими регламентами замовника.</p>
              </div>
            </div>

            <div className="block faq" data-aos="fade-up">
              <h3>FAQ</h3>
              {FAQ_EXTRA.map((f, i)=>(
                <div className="q" key={i} data-aos="fade-up" data-aos-delay={i*40}>
                  <button aria-expanded={openFaq===i} onClick={()=> setOpenFaq(openFaq===i ? -1 : i)}>
                    <span>{f.q}</span>
                    <span>{openFaq===i ? '−' : '+'}</span>
                  </button>
                  {openFaq===i && <div className="a">{f.a}</div>}
                </div>
              ))}
            </div>

            <div className="block" data-aos="fade-up">
              <h3>Готові обговорити ваш об’єкт?</h3>
              <p>Надішліть план/фото та коротке ТЗ — підготуємо рішення і кошторис.</p>
              <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                <Link className="btn btn--primary" to="/contacts">Зв’язатися</Link>
                <Link className="btn btn--ghost" to="/articles">Почитати кейси</Link>
              </div>
            </div>
          </Extra>
        </div>
      </Layout>
    </Page>
  );
}
